<template>
    <div
    v-for="(product, index) in products"
    :key="index"
    class="product-card"
    :style="`animation-duration: ${(index / 5) + 2}s;`"
    @click="emit('cardClicked', product)">
        <div class="info-wrapper">
            <div class="product-info">
                <div class="color-square" :style="{backgroundColor: colors[index]}"></div>
                <div class="description">{{ product.description }}</div>
            </div>
            <div
            v-if="selectedTab === 'assetsVsLiabilities' && selectedCurrency !== 'allCurrencies'"
            class="total-amount-wrapper">
                <div class="amount-icon" :class="{'negative': totalAmount(product) < 0}">
                    <component :is="amountIcon(product)"/>
                </div>
                <div class="total-amount">
                    {{ amountFormat(Math.abs(totalAmount(product)), selectedCurrency) }}
                </div>
            </div>
        </div>
        <icon-arrow-right v-if="selectedTab !== 'assetsVsLiabilities'" class="go-to-details-icon" />
    </div>
</template>

<script setup>
import IconArrowRight from '@/assets/icons/arrow-right-full.svg?inline'
/* Vue & Assets */
import { defineAsyncComponent } from 'vue'
import { calculateProductTotalAmountByCurrency } from '@/assets/scripts/parsers/patrimony'
import { amountFormat } from '@/assets/scripts/amounts'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@colors {array} - array with products colors
@products {array} - array with products
@selectedTab {string} - active tab description
@selectedCurrency {string} - selected currency in our picker
@selectedProduct {string} - selected product in our picker
*/
const props = defineProps({
    colors: Array,
    products: Array,
    selectedTab: String,
    selectedCurrency: String,
    selectedProduct: String
})

const emit = defineEmits(['cardClicked'])

/* mapGetters */
const { getPatrimonyAccountsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)

const amountIcon = product => {
    const totalAmount = calculateProductTotalAmountByCurrency(getPatrimonyAccountsList.value(props.selectedCurrency, props.selectedProduct, product.description === 'Activos' ? 'assets' : 'liabilities'))

	if (!totalAmount || totalAmount === 0) return null
	const icon = totalAmount > 0 ? 'plus' : 'minus'
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}

const totalAmount = product => {
    const totalAmount = calculateProductTotalAmountByCurrency(getPatrimonyAccountsList.value(props.selectedCurrency, props.selectedProduct, product.description === 'Activos' ? 'assets' : 'liabilities'))

    return totalAmount
}
</script>

<style lang="scss" scoped>
.product-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;

    width: 100%;
    height: 80px;

    padding: 20px 20px 20px 16px;
    border-radius: 8px;

    background-color: var(--secondary-light-4);
    box-shadow: var(--box-shadow-2);

    transform: translateX(100vw);
    animation-name: slide-left;
    animation-delay: 0.2s;
    animation-iteration-count: 1;
    animation-fill-mode: both;

    @keyframes slide-left {
        0% {
            transform: translateX(100vw);
        }
        100% {
            transform: translateX(0vw);
        }
    }

    .info-wrapper {
        display: flex;
        flex-direction: column;
        gap: 8px;

        overflow: hidden;

        .product-info {
            display: flex;
            align-items: center;
            gap: 8px;

            .color-square {
                flex-shrink: 0;

                width: 16px;
                height: 16px;
                border-radius: 50%;
            }

            .description {
                @include H7-Regular;

                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                color: var(--secondary-dark-4);
            }
        }

        .total-amount-wrapper {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            gap: 3px;

            padding-left: 24px;

            overflow: hidden;

            .amount-icon {
                flex-shrink: 0;

                svg {
                    width: 8px;
                    height: 8px;

                    :deep(.change-color-fill) {
                        fill: var(--positive);
                    }
                }

                &.negative {
                    svg {
                        :deep(.change-color-fill) {
                            fill: var(--negative);
                        }
                    }
                }
            }

            .total-amount {
                @include H8-Regular;
                color: var(--secondary-dark-1);

                overflow:hidden;
                text-overflow:ellipsis;
                white-space:nowrap;
            }
        }
    }

    svg.go-to-details-icon {
        flex-shrink: 0;

        width: 16px;
        height: 16px;

        :deep(.change-color-fill) {
            fill: var(--secondary-dark-2);
        }
    }
}
</style>
